import React, { useEffect, type PropsWithChildren } from 'react';
import {
	ContextualAnalyticsData,
	FireScreenAnalytics,
	SCREEN,
} from '@atlassian/jira-product-analytics-bridge';
import {
	PRIORITY_SCHEME_MODAL_DETAILS_TAB_INDEX,
	PRIORITY_SCHEME_MODAL_PROJECTS_TAB_INDEX,
} from '../../common/ui/constants';
import type {
	ModalOnCompleteStatus,
	CreateOrEditPriorityScheme,
	PrioritySchemeFormState,
} from '../../common/ui/types';
import { ModalView } from './modal-view';
import { PrioritySchemeDetails } from './priority-scheme-details/main';
import { ProjectPicker, type ProjectPickerProps } from './project-picker';
import { type Fields, generateScreenEventMeta } from './utils';

type PrioritySchemeModalWithAnalyticsProps = {
	initialPriorityScheme: CreateOrEditPriorityScheme;
};

export const PrioritySchemeModalWithAnalytics = (
	props: PropsWithChildren<PrioritySchemeModalWithAnalyticsProps>,
) => {
	const { initialPriorityScheme, children } = props;

	return (
		<ContextualAnalyticsData
			{...generateScreenEventMeta(initialPriorityScheme)}
			sourceType={SCREEN}
		>
			<FireScreenAnalytics />
			{children}
		</ContextualAnalyticsData>
	);
};

export type PrioritySchemeDetailsFormProps = {
	renderPrimaryButton?: () => JSX.Element;
	formTab: number;
	formError: Fields;
	isCreatingNewScheme: boolean;
	onComplete: (status: (typeof ModalOnCompleteStatus)[keyof typeof ModalOnCompleteStatus]) => void;
	onPrioritySchemeDetailsChange: (prioritySchemeDetails: CreateOrEditPriorityScheme) => void;
	onSave: () => void;
	projectPickerProps: ProjectPickerProps;
	priorityScheme: CreateOrEditPriorityScheme;
	prioritySchemeModalState: PrioritySchemeFormState;
	setFormTab: (tab: number) => void;
	/**
	 * Given a priority Id, remove all mappings that are mapped to that priority
	 * @param removedPriorityId the priority that was removed from mapping list
	 */
	onRemovePriorityMapping?: (removedPriorityId: string) => void;
};

export const PrioritySchemeDetailsForm = ({
	renderPrimaryButton,
	formTab,
	formError,
	isCreatingNewScheme,
	onComplete,
	onPrioritySchemeDetailsChange,
	onSave,
	projectPickerProps,
	priorityScheme,
	prioritySchemeModalState,
	setFormTab,
	onRemovePriorityMapping,
}: PrioritySchemeDetailsFormProps) => {
	useEffect(() => {
		if (formError.name || formError.description || formError.priorityIds) {
			setFormTab(PRIORITY_SCHEME_MODAL_DETAILS_TAB_INDEX);
		} else if (formError.projectIds) {
			setFormTab(PRIORITY_SCHEME_MODAL_PROJECTS_TAB_INDEX);
		}
	}, [formError, setFormTab]);

	const renderDetailsContent = () => (
		<PrioritySchemeDetails
			prioritySchemeFormState={prioritySchemeModalState}
			priorityScheme={priorityScheme}
			setPriorityScheme={onPrioritySchemeDetailsChange}
			nameError={formError.name}
			descriptionError={formError.description}
			prioritiesError={formError.priorityIds}
			onRemovePriorityMapping={onRemovePriorityMapping}
		/>
	);

	const renderProjectsContent = () => <ProjectPicker {...projectPickerProps} />;

	return (
		<ModalView
			renderPrimaryButton={renderPrimaryButton}
			prioritySchemeFormStatus={prioritySchemeModalState}
			onSubmit={onSave}
			onClose={onComplete}
			isCreatingNewScheme={isCreatingNewScheme}
			tabIndex={formTab}
			setTabIndex={setFormTab}
			renderDetailsContent={renderDetailsContent}
			renderProjectsContent={renderProjectsContent}
		/>
	);
};
